/**
 * ClinicSwitcher.tsx — the waffle-style clinic switcher at the rail top.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 5 (app-shell
 * redesign, 2026-07-21 — replaces the Wave-3 header `<select>` switcher).
 *
 * WHY: the CTO's Wave-5 shell puts product switching where Agency Accelerator
 * puts it — a waffle (dot-grid) trigger at the TOP of the staff rail, not a
 * form control in a page header. The trigger shows the ACTIVE clinic's name
 * beside the waffle glyph and opens the shared accessible {@link Menu} listing
 * every clinic on the roster: Weight Loss is live today; coming-soon clinics
 * are DISABLED, greyed rows (UX Wave 8 §3 — no static "(coming soon)" copy in
 * the visible label any more): they lead with the ⏳ hourglass and carry a
 * `title` tooltip "Coming soon"; the words live on as a screen-reader-only
 * suffix so the signal is never the glyph/colour alone. A deep link
 * (`?clinic=<slug>`) still lands on the friendly ComingSoonPanel — the menu
 * just no longer navigates to a dead product. Selection drives
 * `?clinic=<slug>` (useSearchParams) so the active clinic is part of the URL —
 * shareable, bookmarkable, and read directly by AdminPage (defaulting to
 * Weight Loss). The active clinic carries a leading ✓ in its own item text.
 */
import type { CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Menu, MenuItem } from '../ui/Menu';
import { CheckIcon, HourglassIcon } from '../ui/kit';
import { ADMIN_CLINICS, DEFAULT_CLINIC_SLUG, findClinic } from './clinics';

/** The 3×3 waffle glyph — decorative; the trigger's aria-label names it. */
function WaffleGlyph() {
  return (
    <span
      aria-hidden="true"
      style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 4px)', gap: '3px', flex: 'none' }}
    >
      {Array.from({ length: 9 }).map((_, index) => (
        <span
          key={index}
          style={{ width: '4px', height: '4px', borderRadius: 'var(--gps-r-pill)', background: 'currentColor' }}
        />
      ))}
    </span>
  );
}

/** The trigger chrome — a quiet outlined row on the rail's deep-green ground. */
const waffleTriggerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '44px',
  padding: '8px 10px',
  border: '1px solid rgba(255, 255, 255, 0.35)',
  borderRadius: 'var(--gps-r-md)',
  background: 'rgba(255, 255, 255, 0.06)',
  color: 'var(--gps-on-accent)',
  font: 'inherit',
  fontSize: '13.5px',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
};

export function ClinicSwitcher() {
  const [params, setParams] = useSearchParams();
  const selected = params.get('clinic') ?? DEFAULT_CLINIC_SLUG;
  const current = findClinic(selected);

  function choose(slug: string) {
    const next = new URLSearchParams(params);
    next.set('clinic', slug);
    setParams(next, { replace: true });
  }

  return (
    <Menu
      menuLabel="Clinic switcher"
      triggerLabel="Switch clinic"
      align="start"
      triggerStyle={waffleTriggerStyle}
      menuStyle={{ minWidth: '14rem' }}
      triggerContent={
        <>
          <WaffleGlyph />
          {/* The rail hides this name in its collapsed (icon-only) state —
              see tokens.css `[data-collapsed='true']`. */}
          <span
            className="gps-rail-clinic-name"
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {current?.name ?? selected}
          </span>
        </>
      }
    >
      {({ close }) => (
        <>
          {ADMIN_CLINICS.map((clinic) => (
            <MenuItem
              key={clinic.slug}
              // Wave 8 §3: a coming-soon clinic is not a destination — the row
              // is disabled (greyed by MenuItem) with a "Coming soon" tooltip.
              disabled={!clinic.configured}
              title={clinic.configured ? undefined : 'Coming soon'}
              onSelect={() => {
                choose(clinic.slug);
                close();
              }}
            >
              {/* Wave 8 §3: CheckIcon marks the active clinic; HourglassIcon is the
                  decorative coming-soon signal — words survive sr-only. */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {clinic.slug === selected ? <CheckIcon size={12} /> : null}
                <span>{clinic.name}</span>
                {clinic.configured ? null : (
                  <>
                    <HourglassIcon size={12} />
                    <span className="gps-sr-only">(coming soon)</span>
                  </>
                )}
              </span>
            </MenuItem>
          ))}
        </>
      )}
    </Menu>
  );
}
